import dayjs from 'dayjs';
import { find, isEmpty, toNumber, toString, uniq } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import {
  DEFAULT_POSITION_ALL,
  LISTVIEW_MAX_ROWS_PER_PAGE,
} from 'src/constants';
import {
  useGetAllDayparts,
  useGetDaypartsByLocationGroup,
  useGetLocation,
  useGetPaginatedDefaultAssemblies,
  useGetProjectConfig,
  useParamQuery,
  useViewAssemblyComponents,
} from 'src/hooks';
import { useGetPaginatedSchedules } from 'src/hooks/api/schedules/useGetAllSchedules';
import { useApiErrorNavigator } from 'src/hooks/navigation/useApiErrorNavigator';
import { IDaypart } from 'src/interfaces';
import { formatDate } from 'src/utils';
import { formatTimestamp } from 'src/utils/date';

import { VIEW_OPTIONS } from '../../scheduleLocationGroupPage/modules/LocationSchedulePageTable/LocationSchedulePageTable.interface';
import { formatSchedules } from '../ScheduleLocationPageOO.helper';
import { OOLocationSchedulePageFormValues } from '../ScheduleLocationPageOO.interface';

export const useOOLocationSchedulePage = () => {
  const { locationCode = '' } = useParams();
  const { userRole } = useContext(AuthDataProvider);

  const { getParamsValue, onChangeParams } = useParamQuery();

  const locationGroupId = toNumber(getParamsValue('groupID').join());
  const [rowsPerPage, setRowsPerPage] = useState(LISTVIEW_MAX_ROWS_PER_PAGE);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    daypartId: 1,
    position: DEFAULT_POSITION_ALL,
  });
  const [selectedOption, setSelectedOption] = useState<string>(
    VIEW_OPTIONS.SCHEDULE_COMPONENT,
  );
  const { data: daypartOptions, isLoading: isLoadingDayparts } =
    useGetAllDayparts();
  const {
    data: location,
    isLoading: isLocationLoading,
    error,
  } = useGetLocation(locationCode, userRole);
  const {
    data: activeDaypartOptions = [],
    isLoading: isLoadingActiveDayparts,
  } = useGetDaypartsByLocationGroup(
    [`location_group_id=${locationGroupId}`, 'valid_to.null=true'],
    Boolean(locationGroupId),
  );
  const matchingLocationGroup = find(location?.locationGroups, {
    id: locationGroupId,
  });
  const channelName = matchingLocationGroup
    ? matchingLocationGroup.channel_name
    : null;
  const isDefaultAssembly = selectedOption === VIEW_OPTIONS.DEFAULT_ASSEMBLY;
  const [sortKey, setSortKey] = useState<string>('modified_at');
  const [sortDirection, setSortDirection] = useState<string>('descending');
  const [sortSelected, setSortSelected] = useState(false);

  const sortParam = `${sortDirection === 'descending' ? '-' : ''}${sortKey}`;

  const {
    data: {
      defaultAssemblyData = [],
      totalPages = 1,
      totalCount: totalCountDefault = 1,
    } = {},
    isLoading: locationGroupScheduleLoading,
  } = useGetPaginatedDefaultAssemblies(
    [
      `location_group_id=${locationGroupId}`,
      `page=${page}`,
      `daypart_id=${filters.daypartId}`,
      `sort=${sortParam}`,
      `pageSize=${rowsPerPage}`,
      `currentDate=${dayjs().format('YYYY-MM-DD')}`,
    ],
    Boolean(locationGroupId) && isDefaultAssembly,
  );
  const { data: projectConfig } = useGetProjectConfig();
  const {
    data: {
      schedulesData = [],
      totalSchedulesPages = 1,
      totalCount: totalCountSchedule = 1,
    } = {},
    isLoading: isLoadingSchedules,
  } = useGetPaginatedSchedules(
    [
      `location_group_id=${locationGroupId}`,
      `location_code=${locationCode}`,
      `sort=${sortParam}`,
      `page=${page}`,
      `daypart_id=${filters.daypartId}`,
      `position=${filters.position}`,
      `pageSize=${rowsPerPage}`,
    ],
    Boolean(locationGroupId) && !isDefaultAssembly,
  );
  useEffect(() => {
    setPage(1);
    setFilters({
      daypartId: 1,
      position: DEFAULT_POSITION_ALL,
    });
  }, [selectedOption]);
  useApiErrorNavigator(error);
  const { data: assemblies = [] } = useViewAssemblyComponents(
    [
      `id.list=${uniq(
        defaultAssemblyData.map((data) => data.assemblyData.id),
      ).join('|')}`,
      'omitCovered=true',
    ],
    !isEmpty(defaultAssemblyData),
  );

  const formMethods = useForm<OOLocationSchedulePageFormValues>({
    values: {
      location: location,
    },
  });
  const locationGroup = (
    formMethods.getValues().location?.locationGroups ?? []
  ).find((group) => group.id === locationGroupId);
  const individualLocationScheduleData = formatSchedules(
    defaultAssemblyData,
    assemblies,
    locationCode,
  );
  const shouldShowListview = {
    daypartDisabled:
      !isLoadingActiveDayparts && activeDaypartOptions.length === 0,
    noDefaultAssembly:
      !isLoadingActiveDayparts &&
      !locationGroupScheduleLoading &&
      isDefaultAssembly &&
      activeDaypartOptions.length > 0 &&
      defaultAssemblyData.length === 0,
    noScheduleComponent:
      !isLoadingActiveDayparts &&
      !isLoadingSchedules &&
      !isDefaultAssembly &&
      activeDaypartOptions.length > 0 &&
      schedulesData.length === 0,
  };
  const onChangeLocationGroup = (
    event: React.SyntheticEvent,
    newValue: string,
  ) => {
    onChangeParams('groupID', newValue, true);
  };

  useEffect(() => {
    if (!isEmpty(location?.locationGroups) && !locationGroupId) {
      onChangeParams(
        'groupID',
        toString(location?.locationGroups?.[0].id),
        true,
      );
    }
  }, [location?.locationGroups, locationGroupId, onChangeParams]);

  const isLoading =
    (Boolean(locationGroupId) && locationGroupScheduleLoading) ||
    isLocationLoading;
  const handleDaypartChange = (newDaypartId: number) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, daypartId: newDaypartId }));
  };
  const handlePositionChange = (newPositionId: string) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, position: newPositionId }));
  };
  const handleRowsPerPageChange = (newValue: number) => {
    setRowsPerPage(newValue);
    setPage(1); // reset to first page on page size change
  };
  const formattedSchedulesData = (schedulesData || []).map((item) => ({
    ...item,
    start_date: formatDate(item.start_date as string),
    end_date: formatDate(item.end_date as string),
    modified_at: formatTimestamp(item?.modified_at || '') || '',
    latest_override_date: item.latest_override_date
      ? formatTimestamp(item.latest_override_date as string)
      : '',
    scheduleItems: (item.scheduleItems || []).map((scheduleItem) => ({
      ...scheduleItem,
      start_date: formatDate(scheduleItem.start_date),
      end_date: formatDate(scheduleItem.end_date as string),
      modified_at: '',
      overrides: (scheduleItem.overrides || []).map((override) => ({
        ...override,
        start_date: formatDate(override.start_date),
        end_date: formatDate(override.end_date as string),
        created_at: override.created_at
          ? formatTimestamp(override.created_at as string)
          : '',
      })),
    })),
  }));

  return {
    formMethods,
    individualLocationScheduleData,
    formattedSchedulesData,
    isLoading,
    isLoadingSchedules,
    onChangeLocationGroup,
    channelName,
    page,
    totalPages: totalPages,
    totalSchedulesPages,
    onPageChange: (_: unknown, newPage: number) => setPage(newPage),
    selectedDaypartId: filters.daypartId,
    selectedPositionId: filters.position,
    daypartOptions: daypartOptions as IDaypart[],
    isLoadingDayparts: isLoadingDayparts,

    onDaypartChange: handleDaypartChange,
    onPositionChange: handlePositionChange,
    selectedOption: selectedOption,
    onSelectedOptionChange: (newSelectedOption: string) => {
      setSelectedOption(newSelectedOption);
      setSortSelected(false);
    },
    projectConfig: projectConfig,
    isDefaultAssembly: isDefaultAssembly,
    locationGroup,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection,
    setPage,
    sortSelected,
    setSortSelected,
    totalCountDefault,
    totalCountSchedule,
    rowsPerPage,
    handleRowsPerPageChange,
    shouldShowListview,
  };
};
